import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useFirebase } from 'components/Firebase/index.js';
import EventEmitter from 'eventemitter3';
import { ONE_TO_MANY_PRODUCT, ONE_TO_ONE_PRODUCT, ON_DEMAND_PRODUCT, CHAT_PRODUCT } from 'constants/products.js';
import { isProduction } from 'constants/gcp.js';

const CACHE_KEY = 'lastSeenProductUpdateItemCreatedAt';

const globalStateEmitter = new EventEmitter();

const PRODUCT_TAG_MAP = {
  Live: ONE_TO_MANY_PRODUCT,
  'Shoppable Videos': ON_DEMAND_PRODUCT,
  'Video Consultation': ONE_TO_ONE_PRODUCT,
  Chat: CHAT_PRODUCT,
};

const getLastSeenItemCreatedAt = () => {
  if (!window.localStorage) return null;
  const cached = window.localStorage.getItem(CACHE_KEY);
  if (cached === null) return 0;
  const parsedNumber = parseInt(cached, 10);
  if (isNaN(parsedNumber)) return 0;
  return parsedNumber;
};

const persistLastSeenItemCreatedAt = (createdAt) => {
  if (!window.localStorage) return;
  window.localStorage.setItem(CACHE_KEY, createdAt);
  globalStateEmitter.emit('lastSeenItemCreatedAt', createdAt);
};

const isEnabled = (() => {
  // Disable the product updates module if running in the test suite
  return !window.navigator?.userAgent?.includes('BambuserLiveShopping');
})();

const imageRegex = /<img[^>]+src="([^"]+)"\s*\/>/g;

const useProductUpdates = () => {
  const firebase = useFirebase();
  const [ items, setItems ] = useState([]);
  const [ lastSeenItemCreatedAt, setLastSeenItemCreatedAt ] = useState(getLastSeenItemCreatedAt());

  const { isLoading, isError, data: loadedItems } = useQuery({
    queryKey: ['productUpdates'],
    staleTime: 1000 * 60 * 60,
    queryFn: async () => {
      if (!isEnabled) return [];
      const agent = await firebase.getAgent();
      const { body } = await agent.get('/productUpdates');

      // Modify the response a bit
      return ((Array.isArray(body) && body )|| []).map((it) => {
        let { text } = it;

        // Inject target="_blank" into all <a> tags
        text = text?.replace(/<a /g, '<a target="_blank" ');

        // Extract all img tags from the text
        const imageTags = text?.match(imageRegex) || [];
        const images = [];
        for (const imageUrl of imageTags) {
          const imageSrc = (imageUrl.match(/src="([^"]+)"/)[1] || '').replace(/amp;/g, '');
          text = text.replace(imageRegex, '');
          images.push(imageSrc);
        }

        // Use the announcement date if it exists, otherwise use the creation date
        const createdAt = it.announcementDate ? new Date(it.announcementDate).getTime() : new Date(it.date).getTime();

        // In production, hide items that are newer than the current date to prevent spoilers
        if (isProduction && createdAt > new Date()) return null;

        return {
          ...it,
          text,
          images,
          productTag: PRODUCT_TAG_MAP[it.productTag],
          createdAt,
        };
      }).filter(Boolean);
    },
  });

  // Mark all items that are newer than the last seen item (localStorage) as new
  useEffect(() => {
    if (!loadedItems) return;
    loadedItems.forEach((item) => {
      item.isNew = false;
      if (lastSeenItemCreatedAt === 0 || lastSeenItemCreatedAt < item.createdAt) {
        item.isNew = true;
      }
    });
    loadedItems.sort((a, b) => b.createdAt - a.createdAt);
    setItems(loadedItems);
  }, [loadedItems, lastSeenItemCreatedAt]);

  // This is probably hacky but it'll do for now: Use a global event emitter to
  // keep the last seen item in sync between multiple instances of the component
  useEffect(() => {
    const onLastSeenItemCreatedAtChange = (createdAt) => {
      setLastSeenItemCreatedAt(createdAt);
      setItemsNewStatus();
    };
    globalStateEmitter.on('lastSeenItemCreatedAt', onLastSeenItemCreatedAtChange);
    return () => {
      globalStateEmitter.off('lastSeenItemCreatedAt', onLastSeenItemCreatedAtChange);
    };
  }, []);

  const setItemsNewStatus = () => {
    setItems((prevItems) => prevItems?.map((item) => ({ ...item, isNew: false })));
  };

  const setSeenItems = () => {
    if (!items?.length || lastSeenItemCreatedAt === items[0]?.createdAt) return;
    setLastSeenItemCreatedAt(items[0].createdAt);
    persistLastSeenItemCreatedAt(items[0].createdAt);
    setItemsNewStatus();
  };

  return {
    isLoading,
    isError,
    items,
    setSeenItems,
  };
};

export default useProductUpdates;
